import type { FrappeDoc } from 'frappe-js-sdk/lib/db/types'

import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'

import type {
  FrappeDataRequestDoc,
  FrappeDataRequestDocListArgumentList,
  FrappeDataRequestDocListDocTypeName,
  FrappeDataResponseDoc,
  FrappeDataResponseDocList,
  FrappeDataTokenReturnValue,
} from '@/plugins/vuePluginFrappe'

import {
  type FrappeDataInstance,
  getFrappeDataInstance,
  transformFrappeErrorObject,
} from '@/utilities/stores'

type DataInstanceAuthTokenType = FrappeDataInstance['tokenType']
type DataInstanceAxiosInstance = FrappeDataInstance['axios']
type DataInstanceUseAuthToken = FrappeDataInstance['useToken']

type DataDocListRequestDocTypeArgumentList<DocTypeType> =
  FrappeDataRequestDocListArgumentList<DocTypeType>
type DataDocListRequestDocTypeName = FrappeDataRequestDocListDocTypeName
type DataDocListRequestDoc<AdditionalDocTypeFields> = FrappeDataRequestDoc<AdditionalDocTypeFields>
type DataDocListResponseDocList<DocTypeType> = FrappeDataResponseDocList<DocTypeType>
type DataDocResponseDoc<DocTypeType> = FrappeDataResponseDoc<DocTypeType>

type GetDataInstanceAuthTokenFunction = () => FrappeDataTokenReturnValue
type GetDocListFunction = <DocTypeType, DocTypeArgumentType = DataDocListRequestDoc<DocTypeType>>(
  docTypeName: DataDocListRequestDocTypeName,
  docTypeArgumentList: DataDocListRequestDocTypeArgumentList<DocTypeArgumentType>,
) => Promise<DataDocListResponseDocList<DocTypeType>>

type GetDocFunction = <DocTypeType>(
  docTypeName: DataDocListRequestDocTypeName,
  docName: string,
) => Promise<DataDocResponseDoc<DocTypeType>>

/**
 * ! Caution:
 * * all "frappeInstance" function calls must be wrapped by
 * * "transformFrappeErrorObject" function, for consistent error handling
 */

interface FrappeDataAPIStoreReturn {
  createDoc<T>(doctype: string, value: T): Promise<FrappeDoc<T>>
  dataInstanceAuthTokenType: Ref<DataInstanceAuthTokenType>
  dataInstanceAxiosInstance: Ref<DataInstanceAxiosInstance>
  getDataInstanceAuthToken: GetDataInstanceAuthTokenFunction
  getDoc: GetDocFunction
  getDocList: GetDocListFunction
  isDataInstanceUseAuthToken: Ref<DataInstanceUseAuthToken>
  updateDoc<T>(doctype: string, docname: string, value: Partial<T>): Promise<T>
}

const useFrappeDataAPIStore = defineStore('frappeDataAPI', (): FrappeDataAPIStoreReturn => {
  const frappeDataInstance: FrappeDataInstance = getFrappeDataInstance()

  const dataInstanceAuthTokenType = ref<DataInstanceAuthTokenType>(frappeDataInstance.tokenType)
  const dataInstanceAxiosInstance = ref<DataInstanceAxiosInstance>(frappeDataInstance.axios)
  const isDataInstanceUseAuthToken = ref<DataInstanceUseAuthToken>(frappeDataInstance.useToken)

  const getDataInstanceAuthToken: GetDataInstanceAuthTokenFunction = () => {
    let dataInstanceTokenValue: string | undefined = undefined

    if (frappeDataInstance.token) {
      dataInstanceTokenValue = frappeDataInstance.token()
    }

    return dataInstanceTokenValue
  }

  const getDocList: GetDocListFunction = async (docTypeName, docTypeArgumentList) => {
    return await transformFrappeErrorObject(async () => {
      return await frappeDataInstance.getDocList(docTypeName, docTypeArgumentList)
    })
  }

  const getDoc: GetDocFunction = async (docTypeName, docName) => {
    return await transformFrappeErrorObject(async () => {
      return await frappeDataInstance.getDoc(docTypeName, docName)
    })
  }

  const updateDoc = async <T>(doctype: string, docname: string, value: Partial<T>): Promise<T> => {
    return await transformFrappeErrorObject(async () => {
      return await frappeDataInstance.updateDoc(doctype, docname, value)
    })
  }

  const createDoc = async <T>(doctype: string, value: T): Promise<FrappeDoc<T>> => {
    return await transformFrappeErrorObject(async () => {
      return await frappeDataInstance.createDoc(doctype, value)
    })
  }

  return {
    createDoc,
    dataInstanceAuthTokenType,
    dataInstanceAxiosInstance,
    getDataInstanceAuthToken,
    getDoc,
    getDocList,
    isDataInstanceUseAuthToken,
    updateDoc,
  }
})

export { useFrappeDataAPIStore }
